import webvarsConfigurationService from './webvarsConfigurationService'
import {UserPermissions, UserProfile, UserProfileInfo} from 'powtoon-services/current-user'
import {
  ProducerComponentProps, onGeneratePowtoonCompletedParams, createPowtoonParams
} from '../types'
import {getPublishData} from 'powtoon-services/powtoons/api'
import apiConnection from './apiConnection'
import {canGenerateProducerPowtoonFromUserPermissions} from 'powtoon-services/current-user/store/selectors'
import {createLogger} from '../logging'
import {TokenApiData, getApiUserIdToken} from 'powtoon-services/integrations/api-applications/api'
import {trackBiEvent, trackPageView} from './biTracker'

const logger = createLogger('powtoon-producer-integration')

export function calculateProps({
  userProfileData,
  userProfileLoading,
  userPermissions,
  userPermissionsLoading,
  userIdOneTimeTokenLoading,
  userIdOneTimeTokenData,
  subDomain,
  renderComponent
}: {
    userProfileData: { value: UserProfile; status: number } | null,
    userProfileLoading: boolean,
    userPermissions: UserPermissions | null,
    userPermissionsLoading: boolean,
    userIdOneTimeTokenLoading: boolean,
    userIdOneTimeTokenData: TokenApiData | null,
    subDomain: string,
    renderComponent: React.FC<ProducerComponentProps> | Element | null
 }) {
  const unauthenticatedUser = !userProfileLoading && !userProfileData
  const isUserAuthenticated = !unauthenticatedUser

  const isCanGenerateProducerPowtoon = userPermissions ?
    canGenerateProducerPowtoonFromUserPermissions(userPermissions) :
    false

  const canGenerateProducerPowtoon =  unauthenticatedUser ? true : isCanGenerateProducerPowtoon

  const unauthMaxDailyProducerGenerations = webvarsConfigurationService.getWebVar('unauthMaxDailyProducerGenerations')
  const {token: userIdOneTimeToken} = userIdOneTimeTokenData || {token: ''}
  const shouldRender = !userPermissionsLoading && !userProfileLoading && !userIdOneTimeTokenLoading && !!renderComponent

  const calculatedProps = {
    isUserAuthenticated,
    canGenerateProducerPowtoon,
    unauthMaxDailyProducerGenerations,
    shouldRender,
    userIdOneTimeToken: userIdOneTimeToken || '',
    subDomain,
    trackBiEvent,
    trackPageView
  }
  logger.info('calculatedProps', calculatedProps)
  return calculatedProps
}


export const generatePowtoonCompleted =
  async (userProfileData: UserProfileInfo| null, param: onGeneratePowtoonCompletedParams) => {
    const {value: {id}} = userProfileData || {value: {id: null}}
    let publishAllowed = false
    logger.info('utils generatePowtoonCompleted', {userid: id, param})
    if (id){
      const {powtoonId} = param
      try {
        const publishData = await getPublishData(apiConnection)(powtoonId)
        publishAllowed = publishData?.userLicense?.publishAllowed || false
        logger.info('utils generatePowtoonCompleted', {publishAllowed})
      } catch (error) {
        // eslint-disable-next-line max-len
        logger.error(new Error('failed integration between powtoon and provider for producer AI : onGeneratePowtoonCompleted'), error)
      }
      return publishAllowed
    } else {
      // when calling onGeneratePowtoonCompleted with unauthenticated user FE should return canShare = True
      return true
    }

  }

export const getUserIdOneTimeToken = async () => {
  try {
    const response = await getApiUserIdToken(apiConnection)()
    const {token: userIdOneTimeToken} = response || {token: ''}
    return userIdOneTimeToken
  } catch (error) {
    logger.info('userIdOneTimeTokenData error', error)
    return ''
  }
}

const isStringNotEmpty = (str: string) => str && str.trim() !== ''

export enum VALIDATION_TYPE {
  createPowtoonParams,
  onGeneratePowtoonCompletedParams,
}
export const validateInputParams = (
  validationType: VALIDATION_TYPE,
  funcName: string,
  info: createPowtoonParams | onGeneratePowtoonCompletedParams
): boolean => {
  if (!info) {
    logger.error(
      new Error(
        // eslint-disable-next-line max-len
        'validateInputParams : failed integration between powtoon and provider for producer AI ,with missing info object'
      ),
      {validationType, funcName, info}
    )
    return false
  }
  const missingParams: string[] = []

  const checkAndAddMissingParam = (param: string, paramName: string) => {
    if (!isStringNotEmpty(param)) {
      missingParams.push(paramName)
    }
  }

  switch (validationType){
    case VALIDATION_TYPE.createPowtoonParams : {
      const {powtoonId, appId, signature, timestamp} = info as createPowtoonParams
      checkAndAddMissingParam(powtoonId, 'powtoonId')
      checkAndAddMissingParam(appId, 'appId')
      checkAndAddMissingParam(signature, 'signature')
      checkAndAddMissingParam(timestamp, 'timestamp')
      break
    }
    case VALIDATION_TYPE.onGeneratePowtoonCompletedParams:{
      const {powtoonId, appId} = info as onGeneratePowtoonCompletedParams
      checkAndAddMissingParam(powtoonId, 'powtoonId')
      checkAndAddMissingParam(appId, 'appId')
      break
    }

    default:
  }

  if (missingParams.length > 0) {
    const missingValues = `missing params: ${missingParams.join(', ')}`
    logger.error(
      new Error(
        'validateInputParams : failed integration between powtoon and provider for producer AI'
      ), {funcName, missingValues}
    )
    return false
  }

  return true
}